import { StoreOrder, StoreOrderItem } from '../../models';
import { safeImageUrl } from '../../utils/url';
import { formatCurrency, formatInstantDate } from '../../utils/orderFormat';
import { OrderReservationNotice } from './OrderReservationNotice';
import { OrderStatusBadge } from './OrderStatusBadge';
import { getOrderStatusView, ORDER_STATUS } from './orderStatus';

const VISIBLE_ITEMS = 3;

const ACTION_BASE =
  'inline-flex items-center justify-center gap-1.5 min-h-10 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chocolate/40 disabled:opacity-60';
const ACTION_PRIMARY = `${ACTION_BASE} bg-chocolate-dark text-white hover:bg-chocolate-deep`;
const ACTION_OUTLINE = `${ACTION_BASE} border border-champagne-dark bg-white text-text-dark hover:border-chocolate hover:text-chocolate-dark`;

interface OrderCardProps {
  order: StoreOrder;
  pendingReviews: number;
  canceling: boolean;
  onView: () => void;
  onReview: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

function pieceCount(items: StoreOrderItem[]) {
  const total = items.reduce((sum, item) => sum + item.quantity, 0);
  return total === 1 ? '1 peça' : `${total} peças`;
}

function OrderItemRow({ item }: { item: StoreOrderItem }) {
  return (
    <li className="flex items-center gap-3 px-4 sm:px-5 py-3">
      {item.imageUrl ? (
        <img
          src={safeImageUrl(item.imageUrl)}
          alt=""
          loading="lazy"
          className="w-12 h-12 rounded-lg object-cover bg-champagne shrink-0"
        />
      ) : (
        <span className="w-12 h-12 rounded-lg bg-champagne shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-dark truncate">{item.productName}</p>
        <p className="text-xs text-text-medium">
          {item.variantLabel ? `${item.variantLabel} · ` : ''}
          {item.quantity}x {formatCurrency(item.unitPrice)}
        </p>
      </div>
      <p className="text-sm font-semibold text-text-dark tabular-nums shrink-0">{formatCurrency(item.totalPrice)}</p>
    </li>
  );
}

export function OrderCard({ order, pendingReviews, canceling, onView, onReview, onEdit, onCancel }: OrderCardProps) {
  const canReview = order.status === ORDER_STATUS.Completed && pendingReviews > 0;
  const canChange = order.status === ORDER_STATUS.AwaitingPayment;
  const { nextStep } = getOrderStatusView(order, canReview);
  const visibleItems = order.items.slice(0, VISIBLE_ITEMS);
  const hiddenCount = order.items.length - visibleItems.length;
  const titleId = `order-${order.id}-title`;

  return (
    <article aria-labelledby={titleId} className="bg-white rounded-2xl border border-champagne-dark shadow-sm overflow-hidden">
      <div className="px-4 sm:px-5 pt-4 pb-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id={titleId} className="text-base font-semibold text-text-dark">
              Pedido #{order.id}
            </h3>
            <p className="text-sm text-text-medium">
              {formatInstantDate(order.saleDate)} · {pieceCount(order.items)}
            </p>
          </div>
          <p className="text-base sm:text-lg font-bold text-text-dark tabular-nums shrink-0">
            {formatCurrency(order.totalValue)}
          </p>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <OrderStatusBadge order={order} />
          {nextStep && <p className="text-sm text-text-medium">{nextStep}</p>}
        </div>
      </div>

      <OrderReservationNotice order={order} />

      <ul className="border-t border-champagne divide-y divide-champagne/70">
        {visibleItems.map((item, idx) => (
          <OrderItemRow key={`${item.id}-${idx}`} item={item} />
        ))}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onView}
          className="w-full px-4 sm:px-5 py-2.5 text-left text-sm font-medium text-chocolate-dark hover:bg-champagne-light border-t border-champagne/70 transition-colors"
        >
          {hiddenCount === 1 ? 'Ver mais 1 item' : `Ver mais ${hiddenCount} itens`}
        </button>
      )}

      <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3 border-t border-champagne bg-champagne-light/60">
        {canReview && (
          <button type="button" onClick={onReview} className={ACTION_PRIMARY}>
            Avaliar peças
          </button>
        )}
        <button type="button" onClick={onView} className={ACTION_OUTLINE}>
          Ver detalhes
        </button>
        {canChange && (
          <>
            <button type="button" onClick={onEdit} className={ACTION_OUTLINE}>
              Editar pedido
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={canceling}
              className="ml-auto inline-flex items-center min-h-10 px-2 text-sm font-medium text-danger hover:underline underline-offset-4 disabled:opacity-60"
            >
              {canceling ? 'Cancelando...' : 'Cancelar pedido'}
            </button>
          </>
        )}
      </div>
    </article>
  );
}
